import React, { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SketchPageShell, SketchBox, SketchButton } from './sketchy';
import { logError } from '../services/logtail';

function errorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    if (typeof error.data === 'string') return error.data;
    if (error.data && typeof error.data === 'object' && 'message' in error.data) {
      return String((error.data as { message?: string }).message ?? error.statusText);
    }
    return error.statusText || `Error ${error.status}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

function isChunkLoadError(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

function isAuthContextError(message: string): boolean {
  return message.includes('useAuth must be used within an AuthProvider');
}

const RouteErrorFallback: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const message = errorMessage(error);
  const chunkLoad = isChunkLoadError(message);
  const authContext = isAuthContextError(message);
  const showDevDetails = import.meta.env.DEV;

  useEffect(() => {
    logError('Route error', {
      message,
      stack: error instanceof Error ? error.stack : undefined,
      status: isRouteErrorResponse(error) ? error.status : undefined,
    });
  }, [error, message]);

  const title = chunkLoad
    ? t('errors.route.chunkTitle', 'This page needs a refresh')
    : authContext
      ? t('errors.route.sessionTitle', 'Session interrupted')
      : t('errors.route.title', 'Something went wrong');

  const description = chunkLoad
    ? t(
        'errors.route.chunkBody',
        'A newer version of Cassette is available, or the dev server reloaded while you were on this page. Refresh to load the latest code.',
      )
    : authContext
      ? t(
          'errors.route.sessionBody',
          'Your sign-in context was lost — common after a dev reload. Refresh the page or return home to continue.',
        )
      : t(
          'errors.route.body',
          'An unexpected error stopped this page from loading. Try again, or go back to your tracks.',
        );

  return (
    <SketchPageShell className="min-h-screen flex items-center justify-center px-4 py-12">
      <SketchBox className="max-w-md w-full text-center p-8">
        <ExclamationTriangleIcon className="mx-auto h-14 w-14 text-amber-500 mb-4" aria-hidden />
        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-3 scribble-underline inline-block">
          {title}
        </h1>
        <p className="text-sm text-[var(--color-muted)] mb-6">{description}</p>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <SketchButton variant="primary" onClick={() => window.location.reload()} className="px-5 py-2.5">
            {t('errors.route.refresh', 'Refresh page')}
          </SketchButton>
          <SketchButton variant="ghost" onClick={() => navigate('/dashboard')} className="px-5 py-2.5">
            {t('errors.route.dashboard', 'Back to tracks')}
          </SketchButton>
          <SketchButton variant="ghost" onClick={() => navigate('/')} className="px-5 py-2.5">
            {t('errors.route.home', 'Home')}
          </SketchButton>
        </div>

        {showDevDetails && message && (
          <pre
            className="mt-6 text-left text-xs text-[var(--color-muted)] bg-[var(--color-hover)]/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words"
            data-testid="route-error-details"
          >
            {message}
          </pre>
        )}
      </SketchBox>
    </SketchPageShell>
  );
};

export default RouteErrorFallback;
